/**
 * Where a notification's CTA goes.
 *
 * The backend sends a *structured* target, never a pre-built URL and never a
 * display label. Two reasons, both load-bearing:
 *
 *  - URLs change; stored rows outlive them. A row written today must still
 *    resolve after the app renames a query param.
 *  - Using a label as an identifier is banned outright (CLAUDE.md). The CTA's
 *    visible text comes from an i18n key chosen by `kind`, never the other way
 *    round.
 *
 * `target` crosses the wire as a JSON scalar, so nothing upstream type-checks
 * it. `parseNotificationTarget` is the single place that turns untrusted JSON
 * into one of these, and it is total: it never throws and never returns
 * undefined. That is what lets the backend add a target kind before the
 * frontend knows it — the row simply renders without a CTA instead of crashing
 * the feed.
 */

/** Tabs `MeetingIntelligenceSheet` exposes, plus `brief` (a separate sheet). */
export const MEETING_PANELS = [
  /** Opens MeetingBriefingSheet — a DIFFERENT component. See docs/patterns/15-today-page.md. */
  "brief",
  "follow_up",
  "tasks",
  "notes",
  "transcript",
  "summary",
] as const;

export type MeetingPanel = (typeof MEETING_PANELS)[number];

const KNOWN_PANELS: ReadonlySet<string> = new Set(MEETING_PANELS);

export type NotificationTarget =
  /**
   * The Today page.
   *
   * `tab` and `view` are optional: the digests and the calendar milestone are about Today
   * as a whole, while Daily briefing and Action items due are about one tab with the Today
   * range selected. Omitting them lands on Today with whatever the user last had — which is
   * the right behaviour for a pointer, and the reason a producer that predates these still
   * resolves.
   */
  | { kind: "today"; tab?: "meetings" | "tasks" | "leads"; view?: "past" | "today" | "upcoming" }
  | { kind: "person"; personId: string }
  | { kind: "company"; companyId: string }
  | { kind: "deal"; dealId: string }
  /**
   * A single task, opened in TaskDetailSheet via `?taskSheet=`.
   *
   * Distinct from `meeting` + `panel: "tasks"`, which opens task SUGGESTIONS inside a
   * meeting. A suggestion is an unaccepted proposal with no standalone surface; this is a
   * real task that does have one.
   */
  | { kind: "task"; taskId: string }
  /**
   * The deals list. Used by the Sunday pipeline alerts, which are about a SET of deals
   * rather than one — so there is no id to carry, and the CTA opens the list.
   *
   * The spec wants each of these filtered (owner, close-date range, a week-over-week
   * compare). DealsPage holds those as component state with no URL params, so filtering is
   * a follow-up; landing on the unfiltered list is honest in the meantime, where inventing
   * a filter param that nothing reads would silently do nothing.
   */
  | { kind: "deals"; list?: string }
  /**
   * A Settings section, e.g. `?section=integrations`.
   *
   * Used by the CRM-sync alert so it lands where its email CTA does. The section is a plain
   * string rather than a union: Settings owns that list, it changes without notifying the
   * feed, and an unknown value lands on Settings rather than nowhere.
   */
  | { kind: "settings"; section?: string }
  /**
   * A meeting, opened on a specific panel. Task suggestions use
   * `panel: "tasks"` because a task *suggestion* only renders inside the
   * meeting sheet — it is not a task and has no standalone surface.
   */
  | { kind: "meeting"; meetingId: string; panel: MeetingPanel }
  /**
   * A deal suggestion, which resolves through the same `panel=deals` route the
   * Slack digest already uses, so both channels share one code path.
   */
  | { kind: "dealSuggestion"; suggestionId: string; meetingId: string }
  /** Opens the compose dialog addressed to a contact, resolved by id at click time. */
  | { kind: "composeEmail"; contactId: string }
  /**
   * A job-change question about a contact: opens the person sheet with the review dialog on
   * top (`?personSheet=…&suggestion=…`). Both ids required — without the suggestion the CTA
   * would open a sheet with nothing to review. Once resolved, the backend rewrites the row's
   * target to a plain `person`, so a stale "Review change" never leads anywhere dead.
   */
  | { kind: "contactSuggestion"; personId: string; suggestionId: string }
  /** No CTA: an unknown kind, a malformed payload, or a row that is purely informational. */
  | { kind: "none" };

/** The inert target. Exported so callers can compare against it explicitly. */
export const NO_TARGET: NotificationTarget = { kind: "none" };

/** Non-empty string guard — ids arriving as "" or null are as useless as absent. */
function str(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

/**
 * Turn untrusted JSON into a NotificationTarget.
 *
 * Total by construction: every path returns a valid target, and anything
 * unrecognised, malformed, or missing a required id degrades to `NO_TARGET`
 * rather than producing a CTA that would navigate nowhere.
 */
export function parseNotificationTarget(raw: unknown): NotificationTarget {
  if (typeof raw !== "object" || raw == null) return NO_TARGET;

  const t = raw as Record<string, unknown>;

  switch (t.kind) {
    case "today": {
      const tab = str(t.tab);
      const view = str(t.view);
      return {
        kind: "today",
        // Validated, not trusted: an unrecognised tab or range is dropped so the row still
        // opens Today rather than degrading to no CTA at all.
        ...(tab === "meetings" || tab === "tasks" || tab === "leads" ? { tab } : {}),
        ...(view === "past" || view === "today" || view === "upcoming" ? { view } : {}),
      };
    }
    case "settings": {
      const section = str(t.section);
      return section ? { kind: "settings", section } : { kind: "settings" };
    }
    case "deals": {
      // `list` names a built-in deals view (the Sunday alert views). Optional, because an
      // older producer sends a bare `deals` target and must still resolve — to the
      // unfiltered list, which is honest rather than wrong.
      const list = str(t.list);
      return list ? { kind: "deals", list } : { kind: "deals" };
    }
    case "task": {
      const taskId = str(t.taskId);
      return taskId ? { kind: "task", taskId } : NO_TARGET;
    }

    case "person": {
      const personId = str(t.personId);
      return personId ? { kind: "person", personId } : NO_TARGET;
    }

    case "company": {
      const companyId = str(t.companyId);
      return companyId ? { kind: "company", companyId } : NO_TARGET;
    }

    case "deal": {
      const dealId = str(t.dealId);
      return dealId ? { kind: "deal", dealId } : NO_TARGET;
    }

    case "meeting": {
      const meetingId = str(t.meetingId);
      if (!meetingId) return NO_TARGET;
      // An unknown panel is recoverable — the meeting is still worth opening —
      // so fall back to the summary tab rather than dropping the CTA entirely.
      const panel =
        typeof t.panel === "string" && KNOWN_PANELS.has(t.panel)
          ? (t.panel as MeetingPanel)
          : "summary";
      return { kind: "meeting", meetingId, panel };
    }

    case "dealSuggestion": {
      const suggestionId = str(t.suggestionId);
      const meetingId = str(t.meetingId);
      // Both ids are required: the route is meeting-scoped, and without the
      // suggestion id the CTA would open an empty Add Deal form — the exact
      // bug this target kind exists to fix.
      return suggestionId && meetingId
        ? { kind: "dealSuggestion", suggestionId, meetingId }
        : NO_TARGET;
    }

    case "composeEmail": {
      const contactId = str(t.contactId);
      return contactId ? { kind: "composeEmail", contactId } : NO_TARGET;
    }

    case "contactSuggestion": {
      const personId = str(t.personId);
      const suggestionId = str(t.suggestionId);
      return personId && suggestionId ? { kind: "contactSuggestion", personId, suggestionId } : NO_TARGET;
    }

    case "none":
      return NO_TARGET;

    default:
      // A kind this build does not know. The row still renders; it just has no
      // action. This is the seam that lets the backend ship first.
      return NO_TARGET;
  }
}
